// Physics, ported verbatim from the legacy WebGL build's physics.js.
// Equations of motion, constants, and the (slightly weird) per-component
// quadratic spring law are all preserved exactly — the "feel" is the IP, so
// any change here is regression risk for no gain.
//
// Differences from the original:
//   - Uses Three.js Vector3 instead of gl-matrix vec3, so values plug
//     directly into Object3D.position / .quaternion without conversion.
//   - Gravity points along -Y (Three.js Y-up convention) instead of +Z.
//     The physics is rotation-invariant; only the axis label changed.

import { Vector3 } from 'three';

export class PhysicalBody {
	constructor({ position, velocity = new Vector3(), mass = 100.0, gravityAcc = 70 }) {
		this.position = position.clone();
		this.velocity = velocity.clone();
		this.acceleration = new Vector3();
		this.force = new Vector3();
		this.mass = mass;

		// gravityAcc is per-body so a level can dial it down (e.g. the moon).
		// Default 70 preserves the original island feel exactly.
		this.gravityAcc = gravityAcc;
		this.gravityForce = new Vector3(0, -this.mass * this.gravityAcc, 0);
		this.dragCoeff = 0.1;

		// Angular state — kept for future use (rotation comes from mouse-look
		// in Phase 1, not from torque integration).
		this.rotation = new Vector3();
		this.angularVel = new Vector3();
		this.angularAcc = new Vector3();
		this.angularTorque = new Vector3();
		this.inputAngularTorque = new Vector3();
		this.angularTorqueDamping = 20.0;
	}

	vel2pos(dt) {
		this.position.addScaledVector(this.velocity, dt);
	}

	acc2vel(dt) {
		this.velocity.addScaledVector(this.acceleration, dt);
	}

	force2acc() {
		this.acceleration.copy(this.force).multiplyScalar(1 / this.mass);
	}

	updateForce(springForce, tangentialForce) {
		// Air drag: opposes velocity, magnitude proportional to v².
		// |airForce| = dragCoeff * |v|² in the direction of -v̂.
		const airForce = _tmpVec.set(0, 0, 0);
		const speed = this.velocity.length();
		if (speed > 0) {
			airForce.copy(this.velocity).multiplyScalar(-speed * this.dragCoeff);
		}

		this.force
			.copy(this.gravityForce)
			.add(springForce)
			.add(tangentialForce)
			.add(airForce);
	}
}

export class BungeeCord {
	constructor({ ropeLength, ropeOrigin, ropePosition }) {
		this.ropeLength = ropeLength;
		this.ropeOrigin = ropeOrigin.clone();
		this.ropePosition = ropePosition;   // shared reference — updated by caller each frame
		this.springConstant = 1.0;
		this.dampingCoeff = 10.0;
		this.springForce = new Vector3();
		this.tangentialForce = new Vector3();
		this.ropeDisplacement = new Vector3();
	}

	updateSpringForce(velocity) {
		this.ropeDisplacement.subVectors(this.ropePosition, this.ropeOrigin);
		const stretchDist = this.ropeDisplacement.length();
		const springLength = stretchDist - this.ropeLength;

		this.springForce.set(0, 0, 0);

		// Cord is taut only when stretched past its rest length.
		if (springLength > 0) {
			// Legacy spring law: per component,
			//   stretch_i = displacement_i * (springLength / |displacement|)
			//   F_i = -sign(displacement_i) * k * stretch_i²
			// This is non-standard (quadratic per-component norm) but is the
			// exact behavior the original had, so we keep it.
			const ratio = springLength / stretchDist;
			const sx = this.ropeDisplacement.x * ratio;
			const sy = this.ropeDisplacement.y * ratio;
			const sz = this.ropeDisplacement.z * ratio;
			this.springForce.set(
				(this.ropeDisplacement.x > 0 ? -1 : 1) * this.springConstant * sx * sx,
				(this.ropeDisplacement.y > 0 ? -1 : 1) * this.springConstant * sy * sy,
				(this.ropeDisplacement.z > 0 ? -1 : 1) * this.springConstant * sz * sz,
			);

			// Velocity-proportional damping, applied only when taut.
			this.springForce.addScaledVector(velocity, -this.dampingCoeff);
		}
	}

	updateTangentialForce(gravityForce) {
		this.ropeDisplacement.subVectors(this.ropePosition, this.ropeOrigin);
		const stretchDist = this.ropeDisplacement.length();
		const springLength = stretchDist - this.ropeLength;

		if (springLength > 0) {
			// Pendulum restoring force: -(g × r) × r̂, scaled to taste.
			const ropeHat = _tmpVec.copy(this.ropeDisplacement).normalize();
			this.tangentialForce.crossVectors(gravityForce, this.ropeDisplacement);
			this.tangentialForce.cross(ropeHat);
			this.tangentialForce.multiplyScalar(-0.01);
		} else {
			this.tangentialForce.set(0, 0, 0);
		}
	}
}

// Scratch vector, hoisted out of hot paths to avoid per-frame allocations.
const _tmpVec = new Vector3();
